'use client';
import { PromotionStar as PromotionStarIcon } from '@ahafood/icons';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Badge } from '@ahafood/ui/components/badge';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { Clock, MapPin } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  ETABadge,
  RatingBadge,
} from '../../(main)/store/[storeSlug]/components/store-header';
import HighlightItems from '../../(main)/store/components/highlight-items';
import AiSummaryHighlight from '../../cua-hang/components/ai-summary-highlight';
import type { Store } from '@/api/ahafood';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { getFallbackName } from '@/lib/utils';
import { useAuthStore } from '@/store';

type OpenHoursType = Record<string, { start_time: number; end_time: number }[]>;

const formatTime = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

const getCurrentDayHours = (openHours?: OpenHoursType): string => {
  if (!openHours) return 'Đóng cửa';
  const today = new Date().getDay();
  const dayIndex = today === 0 ? 6 : today - 1;
  const dayKey = dayIndex.toString();
  if (!openHours[dayKey]?.length) return 'Đóng cửa';
  return openHours[dayKey]
    .map(
      (range) =>
        `${formatTime(range.start_time)} - ${formatTime(range.end_time)}`
    )
    .join(', ');
};

const StoreHours: React.FC<{ openHours?: OpenHoursType }> = ({ openHours }) => {
  const [currentDayHours, setCurrentDayHours] = useState<string>('Đóng cửa');
  useEffect(() => {
    const updateHours = () => {
      setCurrentDayHours(getCurrentDayHours(openHours));
    };
    updateHours();
    const timer = setInterval(updateHours, 600000);
    return () => clearInterval(timer);
  }, [openHours]);
  return (
    <Badge
      variant="secondary"
      className="w-fit flex-none rounded-sm text-xs md:text-sm py-1 px-1.5 [&_svg]:size-3 [&_svg]:font-bold text-center"
    >
      <Clock className="mr-1 size-3 md:size-4 self-center mb-0.5" />
      <span>{currentDayHours}</span>
    </Badge>
  );
};

const InactiveBadge = () => (
  <div className="absolute inset-0 bg-neutral-50/50 z-10 size-full rounded-xl">
    <Badge className="absolute bg-neutral-90 hover:bg-white top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 px-3 py-1.5 shadow-md md:px-4 md:py-2 md:text-sm">
      <span className="font-bold text-nowrap hover:text-neutral-90">
        Đóng cửa
      </span>
    </Badge>
  </div>
);

type Props = {
  data: Store;
  className?: string;
  position?: number;
};

export default function StoreCard({ data, className, position }: Props) {
  // Add closed state flags
  const { user } = useAuthStore();
  const isClosed = data.is_temporary_closed;
  const router = useRouter();
  const routes = useCityRoutes();
  // Determine disabled classes when closed
  const disabledClasses = isClosed
    ? 'pointer-events-none cursor-not-allowed'
    : '';
  const isMobile = useIsMobile();
  const distance = (distance: number) => {
    return (distance / 1000).toFixed(2);
  };
  const handleCardClick = () => {
    if (isClosed) return;

    trackEvent('click_item_card', {
      action_time: Math.floor(Date.now() / 1000),
      item_type: 'store_item',
      position: position ?? 0,
      distance: data.distance,
      total_sold: data.total_sold,
      rating: data.rating.average,
      address: data.nearest_branch?.address || data.address,
      store_id: data._id,
      store_name: data.name,
      user_id: user?.cunstomerId,
    });

    // Use router.push instead of Link to avoid opening new browser tab
    if (data.slug) {
      router.push(routes.storeDetail(data.slug));
    }
  };

  if (isMobile) {
    return (
      <Card
        onClick={handleCardClick}
        className={cn(
          'w-full flex flex-row md:flex-col items-stretch overflow-hidden md:shadow-sm shadow-none md:hover:shadow-8 border-none rounded-xl md:hover:scale-105 transition-transform',
          className
        )}
        data-testid="home-store-card"
        data-index={position}
        data-item-id={data._id}
      >
        <div className="shrink-0 size-28 md:w-full md:h-auto md:aspect-video relative">
          <Avatar className="size-full md:rounded-none rounded-xl bg-neutral-10">
            {isClosed && <InactiveBadge />}
            <AvatarImage
              className="object-cover"
              src={data.image_url}
              alt={data.name}
              data-testid="home-store-card-image"
            />

            <AvatarFallback className="rounded-none">
              {getFallbackName(data.name)}
            </AvatarFallback>
          </Avatar>
        </div>
        <CardHeader className="flex-1 min-w-0 pl-4 py-0 pr-0 flex flex-col md:gap-2 gap-0 justify-start">
          <CardTitle className="flex flex-row items-center gap-2 min-w-0">
            <h3
              className="flex-1 text-base md:text-lg truncate"
              data-testid="home-store-card-name"
            >
              {data.name}
            </h3>
          </CardTitle>
          <div className="flex flex-col items-start gap-2 ">
            <RatingBadge
              className="border-none px-0"
              rating={data.rating}
              total_sold={data.total_sold}
              data-testid="home-store-card-rating"
            />
            <ETABadge
              cityId={data.city_id}
              distance={data.distance}
              data-testid="home-store-card-eta"
            />
            {/* {data.distance > 0 && (
                <Badge
                  variant="secondary"
                  className="flex-none rounded-sm text-xs md:text-sm bg-primary-10 text-primary-50 py-1 px-1.5 [&_svg]:size-4 [&_svg]:font-bold text-center"
                >
                  <MapPin className="pr-1" />
                  {`${distance(data.distance)}km`}
                </Badge>
              )}
              {data.open_hours && <StoreHours openHours={data.open_hours} />} */}
          </div>
          {!data.highlight_items && (
            <CardDescription
              title={data.address}
              className="truncate text-xs md:text-sm min-w-0"
              data-testid="home-store-card-address"
            >
              {data.address}
            </CardDescription>
          )}
          {data.ai_tags && data.ai_tags.length > 0 && (
            <div className="mt-1 w-full min-w-0">
              <div className="w-full overflow-hidden scrollbar-hide">
                <div
                  className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {data.ai_tags.map((deal, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="shrink-0 bg-yellow-20 px-2 text-xs md:text-sm font-semibold capitalize text-yellow-50 whitespace-nowrap border-none"
                    >
                      <Image
                        src={`${assetPrefix}/static/images/promo.webp`}
                        alt={'chat bot image'}
                        width={16}
                        height={16}
                        className="object-contain mr-1"
                      />
                      {deal}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
          {/* AI Summary or Highlight Items */}
          <AiSummaryHighlight summary={data.summary_review} />
          <div className="w-full min-w-0">
            <HighlightItems items={data.highlight_items ?? []} />
          </div>
        </CardHeader>
      </Card>
      // </div>
    );
  }
  return (
    <div
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      role="button"
      tabIndex={isClosed ? -1 : 0}
      className={`group relative col-span-1 flex flex-col border md:border-none p-3 md:p-0 rounded-xl overflow-hidden w-full text-left hover:bg-gray-50 transition-colors cursor-pointer ${disabledClasses}`}
      data-testid="home-store-card"
      data-index={position}
      data-item-id={data._id}
    >
      <div className="flex flex-row md:flex-col gap-3 md:gap-2 items-start w-full">
        {/* Image */}
        <div className="relative aspect-square w-24 md:w-full md:aspect-[316/200] flex-none overflow-hidden rounded-xl bg-neutral-15">
          <Avatar className="cursor-pointer rounded-xl bg-top object-cover transition duration-200 ease-out group-hover:scale-105 size-full">
            {isClosed && <InactiveBadge />}
            <AvatarImage
              src={
                data.image_url.includes('scontent')
                  ? `${assetPrefix}/static/images/empty-thumbnail.webp`
                  : data.image_url
              }
              alt={data.name}
              className="size-full"
              style={{ objectFit: 'cover' }}
              data-testid="home-store-card-image"
            />

            <AvatarFallback className="size-full">
              {data.name ? data.name.charAt(0) : 'S'}
            </AvatarFallback>
          </Avatar>
        </div>

        {/* Info */}
        <div className="flex flex-col w-full min-w-0">
          {/* Store name & meta */}
          <div className="flex flex-col gap-1">
            <h3
              className="text-left text-lg font-bold text-neutral-90 group-hover:text-primary-60 line-clamp-2"
              data-testid="home-store-card-name"
            >
              {data.name}
            </h3>
            <div className="flex flex-wrap gap-4 text-sm text-neutral-60">
              {data.distance > 0 && (
                <span
                  className="flex items-center gap-1.5 font-medium text-primary-60"
                  data-testid="home-store-card-distance"
                >
                  <MapPin className="size-3 mb-0.5" />
                  {`${distance(data.distance)}km`}
                </span>
              )}
              <StoreHours openHours={data.open_hours} />
            </div>
          </div>

          {/* Badge deals */}
          {data.ai_tags && data.ai_tags.length > 0 && (
            <div className="mt-2 w-full min-w-0">
              <div className="w-full overflow-hidden">
                <div
                  className="flex gap-1.5 md:overflow-x-auto flex-wrap scrollbar-none pb-1"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {data.ai_tags.map((deal, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="shrink-0 border-yellow-30 bg-yellow-10 px-2 text-sm font-semibold capitalize text-yellow-50 whitespace-nowrap"
                    >
                      <PromotionStarIcon className="mr-1 size-4" />
                      {deal}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* AI Summary or Highlight Items */}
          <AiSummaryHighlight summary={data.summary_review} />

          {/* Highlight Items */}
          {data.highlight_items && data.highlight_items.length > 0 && (
            <div className="w-full min-w-0">
              <HighlightItems items={data.highlight_items} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
